/**
 * Caisse (POS) controller — talks to the back-end Caisse / CommandeCaisse / CaisseProduct APIs.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return { Authorization: token ? `Bearer ${token}` : '' };
};

const CaisseController = {
  // --- Caisse sessions ---
  current: async () => {
    const r = await axios.get(`${API_URL}/Caisse/Current`, { headers: authHeaders() });
    return r.data;
  },
  getById: async (id) => {
    const r = await axios.get(`${API_URL}/Caisse/byId/${id}`, { headers: authHeaders() });
    return r.data;
  },
  open: async ({ fondCaisse, depotId, notes, utilisateurId }) => {
    const r = await axios.post(
      `${API_URL}/Caisse/Open`,
      { fondCaisse: Number(fondCaisse) || 0, depotId: depotId ? Number(depotId) : null, utilisateurId: utilisateurId ? Number(utilisateurId) : null, notes },
      { headers: authHeaders() }
    );
    return r.data;
  },
  close: async (id) => {
    const r = await axios.post(`${API_URL}/Caisse/Close/${id}`, {}, { headers: authHeaders() });
    return r.data;
  },
  paginate: async (page = 1, size = 20, params = {}) => {
    const q = new URLSearchParams({ Size: size, PageNumber: page, ...params });
    const r = await axios.get(`${API_URL}/Caisse/Pagination?${q}`, { headers: authHeaders() });
    return r.data;
  },

  // --- CommandeCaisse ---
  paginateCommandes: async (caisseId, page = 1, size = 200, etat = null) => {
    const q = new URLSearchParams({ Size: size, PageNumber: page });
    if (caisseId) q.set('CaisseId', caisseId);
    if (etat) q.set('Etat', etat);
    const r = await axios.get(`${API_URL}/CommandeCaisse/Pagination?${q}`, { headers: authHeaders() });
    return r.data;
  },
  saveCommande: async (entet, line) => {
    const r = await axios.post(
      `${API_URL}/CommandeCaisse/Save`,
      { entet, line },
      { headers: authHeaders() }
    );
    return r.data;
  },
  getCommande: async (id) => {
    const r = await axios.get(`${API_URL}/CommandeCaisse/byId/${id}`, { headers: authHeaders() });
    return r.data;
  },
  deleteCommande: async (id) => {
    const r = await axios.delete(`${API_URL}/CommandeCaisse/${id}`, { headers: authHeaders() });
    return r.data;
  },
  deleteCommandeLine: async (lineId) => {
    const r = await axios.delete(`${API_URL}/CommandeCaisse/line/${lineId}`, { headers: authHeaders() });
    return r.data;
  },

  // --- Exchange (échange): customer returns products and takes others; the
  //     price difference is settled at the caisse.
  echange: async (payload) => {
    const r = await axios.post(
      `${API_URL}/CommandeCaisse/Echange`,
      payload,
      { headers: authHeaders() }
    );
    return r.data;
  },

  // --- POS product search (paginated). When `search` matches a barcode exactly,
  //     the backend returns a single sellable unit immediately.
  searchProducts: async (page = 1, size = 10, search = '', depotId = null) => {
    const q = new URLSearchParams({ Size: size, PageNumber: page });
    if (search) q.set('Search', search);
    if (depotId) q.set('DepotId', depotId);
    const r = await axios.get(`${API_URL}/CaisseProduct/Search?${q}`, { headers: authHeaders() });
    return r.data;
  },

  // --- Chiffre d'affaires (revenue) aggregated per caisse for a date range, optional depot.
  chiffreAffaire: async ({ from = null, to = null, depotId = null } = {}) => {
    const q = new URLSearchParams();
    if (from) q.set('From', from);
    if (to) q.set('To', to);
    if (depotId) q.set('DepotId', depotId);
    const r = await axios.get(`${API_URL}/Caisse/ChiffreAffaire?${q}`, { headers: authHeaders() });
    return r.data;
  },
};

export default CaisseController;
